// @ts-check

/**
 * R57b: the explicit cart-section registry.
 *
 * Cart mutations used to discover what to re-render by querying every
 * cart-items-component on the page and collecting its data-section-id. On
 * build-a-box that meant the header section and the 500 KB grid section came
 * back on every add and remove. Surfaces that have been split into named
 * Section Rendering API files now advertise themselves with a
 * data-cart-section-id attribute ("cart-drawer", "build-a-box-cart",
 * "cart-icon-bubble"), and mutation requests are built from this registry
 * instead.
 *
 * Surfaces that have not been migrated (the quick-shop sidebar, the /cart
 * page) still advertise a template section id on their cart-items-component;
 * those are unioned in so they keep updating exactly as before.
 *
 * R57c: this file is now purely the registry - discovery and counting. The
 * post-mutation pipeline moved to assets/cart-controller.js, where the fetch
 * and the error path live with it. applyMutation stays here as a delegating
 * shim because inline section scripts still call it by that name.
 */
(function () {
  function ids() {
    const out = new Set();

    document.querySelectorAll('[data-cart-section-id]').forEach((el) => {
      const id = el.getAttribute('data-cart-section-id');
      if (id) out.add(id);
    });

    document.querySelectorAll('cart-items-component[data-section-id]').forEach((el) => {
      if (el.hasAttribute('data-cart-section-id')) return;
      if (el.closest('[data-cart-section-id]')) return;
      const id = el.getAttribute('data-section-id');
      if (id) out.add(id);
    });

    return Array.from(out);
  }

  /**
   * Reads the post-mutation item count from a sections response. Prefers the
   * data-cart-state blob (rendered even when the cart is empty), then the
   * cartItemCount ref, then zero.
   * @param {Record<string, string>} sections
   * @returns {number}
   */
  function readItemCount(sections) {
    for (const html of Object.values(sections || {})) {
      if (typeof html !== 'string') continue;
      const doc = new DOMParser().parseFromString(html, 'text/html');

      const state = doc.querySelector('[data-cart-state]');
      if (state) {
        try {
          const parsed = JSON.parse(state.textContent || '');
          if (typeof parsed.item_count === 'number') return parsed.item_count;
        } catch (_) {
          // fall through to the ref lookup
        }
      }

      const count = doc.querySelector('[ref="cartItemCount"]')?.textContent;
      if (count) return parseInt(count, 10) || 0;
    }

    return 0;
  }

  /**
   * R57c: delegates to the controller, which owns the pipeline now. Resolved at
   * call time so the load order of the two files does not matter. The inline
   * fallback covers the case where cart-controller.js failed to load at all -
   * without it a mutation would land server-side and no surface would hear
   * about it.
   * @param {Record<string, string>} sections
   * @param {string} source
   * @returns {number} the post-mutation item count
   */
  function applyMutation(sections, source) {
    if (window.cartController?.applyMutation) {
      return window.cartController.applyMutation(sections, source);
    }

    const itemCount = readItemCount(sections);

    const event =
      typeof window.CartUpdateEvent === 'function'
        ? new window.CartUpdateEvent({}, source, { itemCount, source, sections })
        : new CustomEvent('cart:update', {
            bubbles: true,
            detail: { sourceId: source, data: { itemCount, source, sections } },
          });

    document.dispatchEvent(event);
    window.dispatchEvent(new CustomEvent('cart-drawer-store:update'));

    return itemCount;
  }

  window.cartSections = { ids, readItemCount, applyMutation };
})();
